import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { bootstrapSession } from '@/api/client';
import { queryClient } from '@/api/query-client';
import { router } from '@/router';
import { useAuthStore } from '@/stores/auth-store';
import '@pixlide/ui/src/styles.css';
import { initAppearance } from '@/stores/preferences-store';

// Thème et couleur d'accent de cet appareil, posés avant le premier rendu pour éviter un
// flash du thème clair au chargement.
initAppearance();

/**
 * Restauration de session au démarrage.
 *
 * L'access token vit uniquement en mémoire (voir `auth-store`), il est donc perdu à chaque
 * rechargement. On tente immédiatement un rafraîchissement : le cookie HttpOnly est envoyé
 * automatiquement par le navigateur et la session repart sans que l'utilisateur ait à se
 * reconnecter.
 *
 * `markBootstrapped` est appelé dans TOUS les cas, succès comme échec : c'est ce drapeau
 * qui autorise le routeur à décider, et ne jamais le lever laisserait l'application
 * bloquée sur son écran de chargement.
 */
async function bootstrap(): Promise<void> {
  try {
    await bootstrapSession();
  } finally {
    useAuthStore.getState().markBootstrapped();
  }
}

const container = document.getElementById('root');

if (container === null) {
  throw new Error("L'élément racine #root est absent du document.");
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

// Lancé après le premier rendu : l'interface s'affiche immédiatement (avec son écran de
// chargement) au lieu d'attendre l'aller-retour réseau.
void bootstrap();
